import {
	type CaptureFileJSONObject,
	type DistanceBearingPitch,
	type SubFormSpanTypeState,
	SubFormType,
} from '@ikegps/core-api';
import { IPFSpanType, SpanType } from './integrations';
import type {
	CollectionModelFieldSelectList,
	CollectionModelFieldSubFormInstance,
	CollectionModelFieldTrueSize,
	CollectionModelFieldVector,
	PLAForm,
} from '..';

/**
 * Standardized Span information.
 */
export interface ISpan {
	spanType: SpanType | null;

	spanVector: DistanceBearingPitch | null;

	spanTargetCollectionId: string | null;

	midSpanIkePhotos: CaptureFileJSONObject[];

	// wires: IWire[];
}

/**
 * SpanSubFormAdaptor wraps a SubForm instance whose SubForm type has been set to SPAN.
 * The SubForm instance is then mapped to the ISpan interface.
 */
export class SpanSubFormAdaptor implements ISpan {
	private subFormSpanTypeState: SubFormSpanTypeState;

	/**
	 * @throws The constructor throws an Error if the instance passed into the constructor is not for a Span SubForm.
	 * Be sure to check this condition before instantiating SpanSubFormAdaptor.
	 */
	constructor(public instance: CollectionModelFieldSubFormInstance) {
		if (instance.schema.subFormType !== SubFormType.SPAN) {
			throw new Error("SubForm instance's schema must have type SPAN to be passed into SpanSubFormAdaptor");
		}
		if (!instance.schema.subFormSpanTypeState) {
			throw new Error("SubForm instance's schema is type SPAN but is missing the SubFormSpanTypeState");
		}
		this.subFormSpanTypeState = instance.schema.subFormSpanTypeState;
	}

	public get spanVectorField(): CollectionModelFieldVector {
		return this.instance.getField<CollectionModelFieldVector>(this.subFormSpanTypeState.vectorFieldId);
	}

	public get spanTypeField(): CollectionModelFieldSelectList {
		return this.instance.getField<CollectionModelFieldSelectList>(this.subFormSpanTypeState.typeFieldId);
	}

	get spanType(): SpanType | null {
		switch (this.spanTypeField.value) {
			case IPFSpanType.FORE_SPAN:
			case SpanType.NextPole:
				return SpanType.NextPole;
			case IPFSpanType.BACK_SPAN:
			case SpanType.PreviousPole:
				return SpanType.PreviousPole;
			case IPFSpanType.OTHER:
			case SpanType.Other:
				return SpanType.Other;
			default:
				return null;
		}
	}

	get spanVector(): DistanceBearingPitch | null {
		return this.spanVectorField.value;
	}

	get spanTargetCollectionId(): string | null {
		if (!this.spanVectorField.isCollectionLink()) {
			return null;
		}
		return this.spanVectorField.stateValue?.collectionId ?? null;
	}

	public get spanMidSpanIkePhotoField(): CollectionModelFieldTrueSize | null {
		if (!this.subFormSpanTypeState.midSpanIkePhotoFieldId) {
			return null;
		}
		return this.instance.getField<CollectionModelFieldTrueSize>(this.subFormSpanTypeState.midSpanIkePhotoFieldId);
	}

	get midSpanIkePhotos(): CaptureFileJSONObject[] {
		return this.spanMidSpanIkePhotoField?.value ?? [];
	}
}

/**
 * PLASpanAdaptor wraps a SubForm instance from the Span field of any PLAForm compatible form.
 * The SubForm instance is then mapped to the ISpan interface.
 */
export class PLASpanAdaptor implements ISpan {
	/**
	 * @throws The constructor throws an Error if the SubForm instance passed into the
	 * constructor is not for the Span field of the given PLAForm.
	 * Be sure to check this condition before instantiating SpanSubFormAdaptor.
	 */
	constructor(
		public instance: CollectionModelFieldSubFormInstance,
		public form: PLAForm
	) {
		if (instance.schema.id !== form.spanField.id) {
			throw new Error("SubForm instance's schema must be the SPAN field of the given PLA form");
		}
	}

	public get spanTypeField(): CollectionModelFieldSelectList {
		return this.instance.getField(this.form.spanTypeField.id);
	}

	public get spanVectorField(): CollectionModelFieldVector {
		return this.instance.getField(this.form.spanVectorField.id);
	}

	get spanType(): SpanType | null {
		switch (this.spanTypeField.value) {
			case IPFSpanType.FORE_SPAN:
			case SpanType.NextPole:
				return SpanType.NextPole;
			case IPFSpanType.BACK_SPAN:
			case SpanType.PreviousPole:
				return SpanType.PreviousPole;
			case IPFSpanType.OTHER:
			case SpanType.Other:
				return SpanType.Other;
			default:
				return null;
		}
	}

	get spanVector(): DistanceBearingPitch | null {
		return this.spanVectorField.value;
	}

	get spanTargetCollectionId(): string | null {
		if (!this.spanVectorField.isCollectionLink()) {
			return null;
		}
		return this.spanVectorField.stateValue?.collectionId ?? null;
	}

	public get spanMidSpanIkePhotoField(): CollectionModelFieldTrueSize | null {
		const midSpanPhotoFormField = this.form.spanMidSpanIkePhoto;
		if (!midSpanPhotoFormField) {
			return null;
		}
		return this.instance.getField<CollectionModelFieldTrueSize>(midSpanPhotoFormField.id);
	}

	get midSpanIkePhotos(): CaptureFileJSONObject[] {
		return this.spanMidSpanIkePhotoField?.value ?? [];
	}
}
